import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import RoomCard from '../components/RoomCard';
import RijekaButton from '../components/RijekaButton';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PropertyMap from '../components/PropertyMap';
import { Map, X } from 'lucide-react';

// Helper function to format date from YYYY-MM-DD to DD/MM/YYYY
const formatDate = (dateString) => {
  if (!dateString) return dateString;
  const parts = dateString.split('-');
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return dateString;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PropertiesPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { theme } = useTheme();
  const [searchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showMobileMap, setShowMobileMap] = useState(false);

  const propertyId = searchParams.get('propertyId');
  const adults = parseInt(searchParams.get('adults'));
  const children = parseInt(searchParams.get('children')) || 0;
  const checkIn = searchParams.get('checkIn');
  const checkOut = searchParams.get('checkOut');

  const totalGuests = adults + children;

  const fetchProperties = async (showRefreshIndicator = false) => {
    try {
      if (showRefreshIndicator) setRefreshing(true);
      else setLoading(true);

      const response = await axios.post(`${API}/properties/search`, {
        property_id: propertyId || undefined,
        adults,
        children,
        check_in: checkIn,
        check_out: checkOut
      });

      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
      
      // Don't show error toast on refresh if it's just a rate limit
      if (!showRefreshIndicator || !error.response || error.response.status !== 429) {
        toast.error('Failed to load properties');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchProperties();
    
    // Auto-refresh every 10 seconds
    const interval = setInterval(() => {
      fetchProperties(true);
    }, 10000);

    return () => clearInterval(interval);
  }, [checkIn, checkOut, adults, children, propertyId]);

  const handleSelectRoom = (property, room) => {
    // Close mobile map if open
    setShowMobileMap(false);
    
    const params = new URLSearchParams({
      propertyId: property.id || property.propId,
      roomId: room.id || room.roomId,
      checkIn,
      checkOut,
      adults: adults.toString(),
      children: children.toString(),
      propertyName: property.name || 'Property',
      roomName: room.name || 'Room',
      price: room.price || '0'
    });

    navigate(`/booking?${params.toString()}`);
  };

  // Handle date change from rate accordion - preserve room position
  const handleDatesChange = (newCheckIn, newCheckOut, roomId = null) => {
    // Update URL with new dates and optionally the room to scroll to
    const params = new URLSearchParams(searchParams);
    params.set('checkIn', newCheckIn);
    params.set('checkOut', newCheckOut);
    if (roomId) {
      params.set('scrollToRoom', roomId);
    }
    navigate(`/properties?${params.toString()}`, { replace: true });
  };
  
  // Scroll to specific room after page loads
  const scrollToRoom = searchParams.get('scrollToRoom');
  
  useEffect(() => {
    if (scrollToRoom && !loading && properties.length > 0) {
      // Small delay to ensure DOM is rendered
      const timer = setTimeout(() => {
        const roomElement = document.querySelector(`[data-room-id="${scrollToRoom}"]`);
        if (roomElement) {
          roomElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Trigger expand for this room's accordion
          const expandEvent = new CustomEvent('expandRoomRates', { detail: { roomId: scrollToRoom } });
          window.dispatchEvent(expandEvent);
        }
        // Clean up the scrollToRoom param from URL
        const params = new URLSearchParams(searchParams);
        params.delete('scrollToRoom');
        navigate(`/properties?${params.toString()}`, { replace: true });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [scrollToRoom, loading, properties, searchParams, navigate]);

  // Check if any properties have location data
  const hasMapData = properties.some(p => p.latitude && p.longitude);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--color-background)]">
        <Header />
        <div className="container mx-auto px-4 py-24 text-center" data-testid="loading-indicator">
          <div className="inline-block animate-spin h-16 w-16 border-4 border-[var(--color-primary)] border-r-transparent rounded-full mx-auto"></div>
          <p className="mt-6 text-[var(--color-text-primary)] text-lg font-medium" >{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      {/* Unified Header - Logo Only */}
      <Header />

      {/* Desktop: Split Screen Layout */}
      <div className="lg:flex lg:h-[calc(100vh-80px)]">
        {/* Left Side - Property List */}
        <div className="lg:w-1/2 lg:overflow-y-auto">
          <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-12 lg:max-w-none">
            {/* Header Section */}
            <div className="mb-6 sm:mb-12 bg-white p-4 sm:p-6 md:p-8 shadow-lg border-l-4 border-[var(--color-primary)] rounded-lg">
              {/* Title and Back Button in same line */}
              <div className="flex items-center justify-between mb-4 sm:mb-6 gap-2 sm:gap-4">
                <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[var(--color-text-primary)]">{t('properties.title')}</h2>
                <RijekaButton 
                  variant="outline" 
                  onClick={() => navigate('/')}
                  data-testid="back-button"
                  className="flex items-center gap-1 sm:gap-2 whitespace-nowrap flex-shrink-0 text-xs sm:text-sm px-2 sm:px-4"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  <span className="hidden sm:inline">{t('properties.backToSearch')}</span>
                </RijekaButton>
              </div>
              
              {/* Dates Section */}
              <div className="flex flex-col gap-2 sm:gap-4 text-[var(--color-text-primary)]">
                <div className="flex items-start sm:items-center gap-2 flex-wrap">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-[var(--color-primary)] flex-shrink-0 mt-0.5 sm:mt-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 text-xs sm:text-sm md:text-base">
                    <span className="font-semibold">{formatDate(checkIn)}</span>
                    <span className="text-gray-500">{t('common.to')}</span>
                    <span className="font-semibold">{formatDate(checkOut)}</span>
                  </div>
                </div>
                
                {/* Guests Section */}
                <div className="flex items-start sm:items-center gap-2 flex-wrap">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-[var(--color-primary)] flex-shrink-0 mt-0.5 sm:mt-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 text-xs sm:text-sm md:text-base">
                    <span className="font-semibold">{totalGuests} {totalGuests > 1 ? t('home.guests') : t('home.guest')}</span>
                    <span className="text-gray-500">•</span>
                    <span className="text-gray-600">{adults} {adults > 1 ? t('home.adults') : t('home.adult')}, {children} {children !== 1 ? t('home.children') : t('home.child')}</span>
                  </div>
                </div>
              </div>
              
              <p className="text-xs sm:text-sm text-gray-500 mt-3 sm:mt-4 flex items-start sm:items-center gap-2">
                <svg className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0 mt-0.5 sm:mt-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span className="leading-tight">{t('common.pricesUpdate')}</span>
              </p>
            </div>

            {/* Results */}
            {properties.length === 0 ? (
              <div className="bg-white p-12 text-center shadow-lg rounded-lg" data-testid="no-properties-message">
                <svg className="w-20 h-20 mx-auto mb-6 text-[var(--color-primary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <h3 className="text-2xl font-bold text-[var(--color-text-primary)] mb-3">{t('properties.noProperties')}</h3>
                <p className="text-gray-600 mb-6">{t('properties.tryDifferentDates')}</p>
                <RijekaButton onClick={() => navigate('/')}>
                  {t('common.back')}
                </RijekaButton>
              </div>
            ) : (
              <div className="space-y-12 pb-24 lg:pb-12" data-testid="properties-list">
                {/* Section 1: Available Rooms (minStay satisfied) */}
                {(() => {
                  const availableProperties = properties.map(prop => ({
                    ...prop,
                    available_rooms: prop.available_rooms?.filter(room => !room.min_stay_required) || []
                  })).filter(prop => prop.available_rooms.length > 0);
                  
                  if (availableProperties.length === 0) return null;
                  
                  return availableProperties.map((property, idx) => (
                    <div key={idx} className="space-y-6">
                      {/* Property Header */}
                      <div className="bg-white p-5 shadow-sm border-l-2 border-[var(--color-primary)] rounded-lg">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <h3 className="text-xl font-bold text-[var(--color-text-primary)] mb-3">
                              {property.name || property.property_name || 'Property Name'}
                            </h3>
                            {property.address && (
                              <p className="text-gray-600 text-sm flex items-center gap-2 mb-3">
                                <svg className="w-4 h-4 text-[var(--color-primary)] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                {property.address}
                              </p>
                            )}
                            {property.description && (
                              <p className="text-gray-700 text-sm leading-relaxed mt-2">{property.description}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2 bg-[var(--color-primary)] text-white px-3 py-1.5 text-sm font-semibold whitespace-nowrap flex-shrink-0 rounded-md">
                            {property.available_rooms?.length || 0} {t('common.available')}
                          </div>
                        </div>
                      </div>
                      
                      {/* Room Cards */}
                      {property.available_rooms?.map((room, roomIdx) => (
                        <RoomCard
                          key={roomIdx}
                          room={room}
                          property={property}
                          onSelect={handleSelectRoom}
                          checkIn={checkIn}
                          checkOut={checkOut}
                          adults={adults}
                          children={children}
                          onDatesChange={handleDatesChange}
                        />
                      ))}
                    </div>
                  ));
                })()}
                
                {/* Section 2: Rooms requiring longer stay (minStay NOT satisfied) */}
                {(() => {
                  const minStayProperties = properties.map(prop => ({
                    ...prop,
                    available_rooms: prop.available_rooms?.filter(room => room.min_stay_required) || []
                  })).filter(prop => prop.available_rooms.length > 0);
                  
                  if (minStayProperties.length === 0) return null;
                  
                  const totalMinStayRooms = minStayProperties.reduce((sum, prop) => sum + prop.available_rooms.length, 0);
                  
                  return (
                    <div className="space-y-6">
                      {/* Section Header */}
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 sm:p-5">
                        <div className="flex items-start gap-3">
                          <svg className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div>
                            <h3 className="text-lg font-bold text-amber-800 mb-1">
                              {t('properties.minStaySection', 'Dostupno uz prilagodbu datuma')}
                            </h3>
                            <p className="text-sm text-amber-700">
                              {t('properties.minStayDescription', `${totalMinStayRooms} smještaj${totalMinStayRooms > 1 ? 'a' : ''} zahtijeva dulji boravak za odabrane datume`)}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      {/* MinStay Room Cards */}
                      {minStayProperties.map((property, idx) => (
                        <div key={`minstay-${idx}`} className="space-y-6">
                          {property.available_rooms?.map((room, roomIdx) => (
                            <RoomCard
                              key={roomIdx}
                              room={room}
                              property={property}
                              onSelect={handleSelectRoom}
                              checkIn={checkIn}
                              checkOut={checkOut}
                              adults={adults}
                              children={children}
                              onDatesChange={handleDatesChange}
                              showMinStayWarning={true}
                            />
                          ))}
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>

        {/* Right Side - Map (Desktop Only) */}
        {hasMapData && (
          <div className="hidden lg:block lg:w-1/2 lg:sticky lg:top-0 lg:h-[calc(100vh-80px)]">
            <PropertyMap
              properties={properties}
              onSelectRoom={handleSelectRoom}
              className="h-full"
              checkIn={checkIn}
              checkOut={checkOut}
            />
          </div>
        )}
      </div>

      {/* Footer - visible on all screen sizes, below main content */}
      <Footer />

      {/* Mobile Map Button */}
      {hasMapData && properties.length > 0 && (
        <button
          onClick={() => setShowMobileMap(true)}
          className="lg:hidden map-mobile-button"
        >
          <Map className="w-5 h-5" />
          {t('map.showMap')}
        </button>
      )}

      {/* Mobile Fullscreen Map */}
      {showMobileMap && (
        <div className="map-fullscreen-overlay lg:hidden">
          <PropertyMap
            properties={properties}
            onSelectRoom={handleSelectRoom}
            isFullscreen={true}
            onClose={() => setShowMobileMap(false)}
            className="h-full"
            checkIn={checkIn}
            checkOut={checkOut}
          />
        </div>
      )}
    </div>
  );
};

export default PropertiesPage;
